import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Wrapper } from './Library.styles'
import { Pagination, SIZE_SMALL, SIZE_MEDIUM, CircularLoading } from '@mds/mds-reactjs-library'
import LibrarySummary from './LibrarySummary'
import LibraryReportRenamePopUp from './LibraryReportRenamePopUp'
import LibraryTableHead from './LibraryTableHead'
import LibraryTableRow from './LibraryTableRow'
import ReportingEndpoints from '../../Services/ReportingEndpoints'
import DataEndpoints from '../../Services/DataEndpoints'
import { useCreateToast } from '../../Hooks/useCreateToast'
import { useLocation } from 'react-router-dom'
import { formatDatasetDisplayName } from '../../Utility/SharedUtility'
import { PublicationDisplayNamesContext, getDisplayNameByPublicationId } from '../../Contexts/PublicationDisplayNames/PublicationDisplayNamesContext'
import LocalStorage, { Keys } from '../../Storage/LocalStorage'
import PortalSearchBar from '../Shared/PortalSearchBar'

const BATCH_SIZE = 10

const Library = () => {
  const [reportRequests, setReportRequests] = useState([])
  const [reportsSummary, setReportsSummary] = useState([])
  const [reportsSummaryToShow, setReportsSummaryToShow] = useState([])
  const [generators, setGenerators] = useState([])
  const [datasets, setDatasets] = useState([])
  const [loading, setLoading] = useState(false)
  const [popUpSummaryEntry, setPopUpSummaryEntry] = useState(null)
  const [showRenameReportPopup, setShowRenameReportPopup] = useState(false)
  const [currentPage, setCurrentPage] = useState(1);
  const [sortSetting, setSortSetting] = useState({ column: 'createdDate', order: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const publicationDisplayNames = useContext(PublicationDisplayNamesContext);

  const { createErrorToast } = useCreateToast();

  useEffect(() => {
    const map = publicationDisplayNames?.publicationDisplayNamesByPublicationId
    if (map && Object.keys(map).length > 0) return
    const rolesRaw = LocalStorage.get(Keys.ROLES)
    const roles = Array.isArray(rolesRaw) ? rolesRaw : (typeof rolesRaw === 'string' ? (() => { try { return JSON.parse(rolesRaw) } catch { return [] } })() : [])
    const isGgcAdmin = roles[0]?.name?.toLowerCase() === 'ggc admin'
    DataEndpoints.getPublications(null, null)
      .then((res) => {
        if (res?.data) publicationDisplayNames?.setPublications(res.data, isGgcAdmin)
      })
      .catch(() => {})
  }, [publicationDisplayNames?.publicationDisplayNamesByPublicationId, publicationDisplayNames?.setPublications])

  useEffect(() => {
    let timerId;
    let isUnmounted = false;
    setLoading(true);
    const getReportRequestsPromise = ReportingEndpoints.getReportRequests();
    const getReportGeneratorsPromise = ReportingEndpoints.getReportGenerators();
    const getDatasetsPromise = DataEndpoints.getDatasets();
    
    getReportGeneratorsPromise
      .then(({ data: generators }) => {
        setGenerators(generators)
      })
      .catch(e => createErrorToast(e));
      
    getDatasetsPromise
      .then(({ data: datasetsData }) => {
        const dsList = Array.isArray(datasetsData) ? datasetsData : (datasetsData?.content ?? datasetsData?.datasets ?? [])
        setDatasets(dsList)
      })
      .catch(e => createErrorToast(e));
      
    getReportRequestsPromise
      .then((res) => {
        let reportRequestRefreshInterval = (+res?.headers?.reportrequestrefreshintervalinseconds * 1000) || 15000;
        if (!isUnmounted)
          timerId = setInterval(updateRecentChanges, reportRequestRefreshInterval);
        setReportRequests(res.data);
      })
      .catch(e => createErrorToast(e));

    Promise.all([getReportRequestsPromise, getReportGeneratorsPromise, getDatasetsPromise])
      .then(() => {
        setLoading(false);
      });
    return () => {
      isUnmounted = true;
      clearInterval(timerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const removeReportRequest = (reportRequestIds) => {
    setReportRequests(prevReports => prevReports.filter(report => !reportRequestIds.includes(report.reportRequestId)));
  }

  const buildReportsSummary = useCallback(() => {
    let reportsSummary = [];
    let generatedDataCounter = 0;
    for (let reportRequest of reportRequests) {
      const displayFromMap = getDisplayNameByPublicationId(publicationDisplayNames, reportRequest.publicationId)
      const datasetName = displayFromMap ?? formatDatasetDisplayName(reportRequest.displayName ?? '')
      if (reportRequest.generatedDataId) {
        let summaryEntry = reportsSummary.find(report => report.generatedDataId === reportRequest.generatedDataId);
        if (summaryEntry) {
          summaryEntry.requests.push(reportRequest);
        }
        else {
          reportsSummary.push({
            reportRequestId: reportRequest.reportRequestId, datasetId: reportRequest.datasetId,
            createdDate: reportRequest.createdDate, name: reportRequest.name,
            datasetName, reportTypeName: reportRequest.reportTypeName,
            sourceName: reportRequest.source, generatedDataId: reportRequest.generatedDataId,
            reportStatusName: reportRequest.reportStatusName, chartName: reportRequest.chartName, requests: [reportRequest]
          });
        }
      }
      else {
        reportsSummary.push({
          reportRequestId: reportRequest.reportRequestId, datasetId: reportRequest.datasetId,
          createdDate: reportRequest.createdDate, name: reportRequest.name,
          datasetName, reportTypeName: reportRequest.reportTypeName,
          sourceName: reportRequest.source, generatedDataId: ++generatedDataCounter, reportStatusName: reportRequest.reportStatusName,
          chartName: reportRequest.chartName, requests: [reportRequest]
        });
      }
    }
    setReportsSummary(reportsSummary);
  }, [reportRequests, publicationDisplayNames]);

  useEffect(() => {
    buildReportsSummary();
  }, [reportRequests, buildReportsSummary]);

  const sortFilterAndSetReportsSummary = useCallback(() => {
    let sortedReportsSummary = sortSetting.order === 'desc' ?
      reportsSummary.sort((a, b) => {
        if (a[sortSetting.column] > b[sortSetting.column])
          return -1;
        if (a[sortSetting.column] < b[sortSetting.column])
          return 1;
        return 0;
      }) :
      reportsSummary.sort((a, b) => {
        if (a[sortSetting.column] < b[sortSetting.column])
          return -1;
        if (a[sortSetting.column] > b[sortSetting.column])
          return 1;
        return 0;
      });
    setReportsSummaryToShow(sortedReportsSummary.filter(summaryEntry => !searchTerm || summaryEntry.name.toLowerCase().includes(searchTerm.toLowerCase())));
  }, [reportsSummary, searchTerm, sortSetting]);

  useEffect(() => {
    sortFilterAndSetReportsSummary();
  }, [reportsSummary, sortFilterAndSetReportsSummary]);

  const updateRecentChanges = () => {
    ReportingEndpoints.getRecentChanges()
      .then(res => {
        setReportRequests(reportRequests => reportRequests.map(reportRequest => {
          const modifiedReportRequest = res.data.find(item => item.reportRequestId === reportRequest.reportRequestId)
          return modifiedReportRequest ? { ...reportRequest, reportStatusName: modifiedReportRequest.reportStatusName, reportFileName: modifiedReportRequest.reportFileName } : reportRequest
        }));
      })
      .catch(e => createErrorToast(e));
  }

  const editReportNameClicked = (summaryEntry) => {
    setShowRenameReportPopup(true);
    setPopUpSummaryEntry(summaryEntry);
  }

  const sortReports = (column, order) => {
    const currentColumn = sortSetting.column;
    const currentOrder = sortSetting.order;

    if (order) {
      setSortSetting({
        column: column,
        order: order
      });
    }
    else if (column === currentColumn) {
      setSortSetting({
        column: column,
        order: currentOrder === 'asc' ? 'desc' : 'asc'
      });
    }
    else {
      setSortSetting({
        column: column,
        order: 'desc'
      });
    }
    sortFilterAndSetReportsSummary();
  }

  const paginationOnChange = (page) => {
    setCurrentPage(page);
  }

  const addReportRequest = (reportRequest) => {
    setReportRequests([...reportRequests, reportRequest]);
  }

  const updateReportName = (reportRequestId, newName) => {
    reportsSummaryToShow.find(summaryEntry => summaryEntry.reportRequestId === reportRequestId).name = newName;
    reportRequests.find(reportRequest => reportRequest.reportRequestId === reportRequestId).name = newName;
  }

  const me = useRef()

  return (
    <Wrapper>
      {
        showRenameReportPopup &&
        <LibraryReportRenamePopUp updateReportName={updateReportName} summaryEntry={popUpSummaryEntry} setShowRenameReportPopup={setShowRenameReportPopup} />
      }
      <LibrarySummary />
      {/* <PortalSearchBar
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          sortFilterAndSetReportsSummary()
        }}
        placeholder="Search library"
        marginTop="24px"
      /> */}
      {
        loading ?
          <div className="tableWrapper">
            <CircularLoading indeterminate size={SIZE_MEDIUM} />
          </div>
          :
          <>
            <div className="tableWrapper">
              <div className="table-actions">
                <p>
                  Showing{' '}
                  {Math.floor(reportsSummaryToShow.length / BATCH_SIZE) === currentPage - 1
                    ? reportsSummaryToShow.length % BATCH_SIZE
                    : BATCH_SIZE}{' '}
                  of {reportsSummaryToShow.length} items
                </p>
              </div>
              <div className="table-container">
                <table ref={me}>
                  <LibraryTableHead sortReports={sortReports} sortSetting={sortSetting} />
                  <tbody>
                    {
                      reportsSummaryToShow.length > 0 ?
                        reportsSummaryToShow.map((summaryEntry, index) => (
                          index >= BATCH_SIZE * (currentPage - 1) && index < currentPage * BATCH_SIZE &&
                          <LibraryTableRow addReportRequest={addReportRequest} key={summaryEntry.generatedDataId} summaryEntry={summaryEntry} generators={generators} removeReportRequest={removeReportRequest} editReportNameClicked={editReportNameClicked} datasets={datasets} />
                        )) :
                        <tr>
                          <td colSpan={6}>No results to show</td>
                        </tr>
                    }
                  </tbody>
                </table>
                <br />
                <Pagination
                  size={SIZE_SMALL}
                  selected={currentPage}
                  total={Math.ceil(reportsSummaryToShow.length / BATCH_SIZE)}
                  onChange={paginationOnChange}
                />
              </div>
              <br />
            </div>
          </>
      }
    </Wrapper>
  )
}

export default Library
